import { CustomToggle } from "./CustomToggle"
import { useEffect, useState } from "react"
import {
    Card,
    CardContent
} from "./ui/card"
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "./ui/chart"
import { Bar, BarChart, CartesianGrid, YAxis, XAxis } from "recharts"
import { ActivityModel, ActivityTimeframeModel } from "../models/activity.model"
import { DataService } from "../services/data.service"
import { ChartBar } from "lucide-react"

export interface ActivityReportProps {
    deploymentId: number
}

export function ActivityReport(props: ActivityReportProps) {
    const [timeframe, setTimeframe] = useState<ActivityTimeframeModel>()
    const [showNumbers, setShowNumbers] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [data, setData] = useState<ActivityModel | null>(null)

    useEffect(() => {
        DataService.getAcitivityReport(props.deploymentId)
            .then(rsp => {
                setData(rsp)
                if (rsp.chart_data.length > 0) {
                    setTimeframe(rsp.chart_data[0])
                }
            })
            .catch((e: any) => setErrorMessage(e.message))
    }, [props.deploymentId])

    function eventsLabel(room: any): string {
        // Server sends "visits" for the Bedroom, "events" for other rooms.
        const raw = room.events_label || 'events'
        return raw.charAt(0).toUpperCase() + raw.slice(1)
    }

    function generateConfig(chart: any): ChartConfig {
        if (showNumbers) return {
            events: {
                label: eventsLabel(chart),
                color: chart.color
            }
        }

        return {
            hours: {
                label: 'Hours',
                color: chart.color
            }
        }
    }

    return (
        <Card className="p-4 shadow-md rounded-lg border" id="activity_report">
            <div className="flex justify-between items-center border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <ChartBar className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Activity Report
                </h3>
            </div>

            {errorMessage ? (
                <div className="text-center mt-4 text-red-500">
                    <p>Error: {errorMessage}</p>
                </div>
            ) : data === null ? (
                <div className="text-center mt-4 text-gray-600">Loading chart data... Please wait!</div>
            ) : (
                <div>
                    <div className="flex justify-center gap-2 mt-4">
                        {data.chart_data.map((interval) => (
                            <button
                                key={interval.name}
                                onClick={() => setTimeframe(interval)}
                                className={`py-2 px-4 text-sm font-medium rounded-lg ${timeframe?.name === interval.name
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                                    }`}
                            >
                                {interval.name}
                            </button>
                        ))}
                    </div>

                    <div className="flex justify-center gap-2 items-center mt-4 border-b pb-4">
                        <span className="text-sm font-medium text-gray-800">Show number of events</span>
                        <CustomToggle checked={showNumbers} onCheckedChange={(checked) => setShowNumbers(checked)} />
                    </div>
                    <CardContent>
                        {timeframe?.rooms.map((room) => {
                            const total = room.data.reduce((chartSum, dataPoint) => {
                                return chartSum + dataPoint[showNumbers ? "events" : "hours"]
                            }, 0)

                            const highestValue = Math.max(
                                ...room.data.map((d) => d[showNumbers ? "events" : "hours"])
                            )

                            room.data = room.data.map((d) => ({
                                ...d,
                                fill: d[showNumbers ? "events" : "hours"] === highestValue ? room.color : "#e5e7eb",
                            }))

                            return (
                                <div className="mb-5" key={room.name}>
                                    <h3 className="pt-3 pb-3 font-medium">
                                        {room.name} ({showNumbers ? `${total} ${eventsLabel(room).toLowerCase()}` : `${total.toFixed(2)}h`})
                                    </h3>
                                    <ChartContainer key={room.name} config={generateConfig(room)}>
                                        <BarChart accessibilityLayer data={room.data}>
                                            <CartesianGrid vertical={false} />
                                            <XAxis
                                                dataKey="title"
                                                tickLine={false}
                                                tickMargin={10}
                                                axisLine={false}
                                                tickFormatter={(value) => value.slice(0, 3)}
                                            />
                                            <YAxis
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={10}
                                            />
                                            <ChartTooltip
                                                cursor={false}
                                                content={<ChartTooltipContent indicator="line" />}
                                            />
                                            <Bar
                                                dataKey={showNumbers ? "events" : "hours"}
                                                radius={4}
                                            />
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                            )
                        })}
                    </CardContent>
                </div>
            )}
        </Card>
    )
}